import { DecoratorBlockNode } from "@lexical/react/LexicalDecoratorBlockNode";
import type { DOMConversionMap, DOMExportOutput, ElementFormatType, NodeKey } from "lexical";
import type { JSX } from "react";
import type { UUID } from "../../../../../utils/uuid";
import { $isDynamicContentNode, DynamicContentNode } from "./dynamic-content-node";
import { convertDynamicContentToolbarElement, isDynamicContentToolbarElement } from "./export";
import type { SerializedDynamicToolbarNode } from "./types";

export class DynamicContentToolbarNode extends DecoratorBlockNode {
  __segment_id: UUID;
  __open: boolean;

  constructor(segment_id: UUID, open: boolean, format?: ElementFormatType, key?: string) {
    super(format, key);
    this.__segment_id = segment_id;
    this.__open = open;
  }

  static getType(): string {
    return "dynamic-toolbar";
  }

  static clone(node: DynamicContentToolbarNode): DynamicContentToolbarNode {
    return new this(node.__segment_id, node.__open, node.__format, node.__key);
  }

  static importJSON(serializedNode: SerializedDynamicToolbarNode): DynamicContentToolbarNode {
    const node = new this(serializedNode.segment_id as UUID, serializedNode.open);
    return node;
  }

  getParentDynamicContentNodeKey(): NodeKey {
    const parentKey = this.getParents().find($isDynamicContentNode)?.getKey();
    if (!parentKey) {
      throw new Error("DynamicContentToolbarNode must be a child of DynamicContentNode");
    }

    return parentKey;
  }

  decorate(): JSX.Element {
    this.getParentDynamicContentNodeKey();
    return <></>;
  }

  exportJSON(): SerializedDynamicToolbarNode {
    return {
      ...super.exportJSON(),
      segment_id: this.__segment_id as string,
      open: this.__open,
      type: "dynamic-toolbar",
      version: 1,
    };
  }

  createDOM(): HTMLElement {
    const dom = document.createElement("div");
    dom.setAttribute("data-segment-id", this.__segment_id as string);
    dom.classList.add("dynamic-toolbar");
    return dom;
  }

  exportDOM(): DOMExportOutput {
    const dom = document.createElement("div");
    dom.setAttribute("data-segment-id", this.__segment_id as string);
    dom.setAttribute("data-node-type", "dynamic-toolbar-node");
    dom.setAttribute("data-open", this.__open ? "true" : "false");
    return { element: dom };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (node: HTMLElement) => {
        if (!isDynamicContentToolbarElement(node)) return null;

        return {
          conversion: (n) => convertDynamicContentToolbarElement(n, DynamicContentToolbarNode),
          priority: 0,
        };
      },
    };
  }

  setOpen(open: boolean): void {
    const writable = this.getWritable();
    writable.__open = open;
  }

  getOpen(): boolean {
    return this.getLatest().__open;
  }

  getSegmentId(): UUID {
    return this.getLatest().__segment_id;
  }

  setSegmentId(segment_id: UUID): void {
    const writable = this.getWritable();
    writable.__segment_id = segment_id;
  }

  toggleOpen(): void {
    this.setOpen(!this.getOpen());
  }

  // Required override
  isShadowRoot(): boolean {
    // This makes the node behave *like* a root node internally for selection
    return true;
  }

  isParentRequired(): true {
    return true;
  }

  createParentElementNode(): DynamicContentNode {
    return new DynamicContentNode(this.__segment_id);
  }
}
